import { Theme } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  RowLayout,
  ColumnLayout,
  Typography,
  Divider,
} from '@/components/atoms';
import { documentIcons } from '@/assets/icons';
import { footerData } from '@/pages/QuickLoan/_mockdata/footerData';
import { formatPhoneNumber } from '@/utils/formatters';

interface FooterConfigParams {
  theme: Theme;
  t: (key: string) => string;
  loanOfficerData?: {
    name?: string;
    email?: string;
    phone?: string;
    nmls?: string;
    title?: string;
  };
  branchData?: {
    name?: string;
    branchNmls?: string;
    email?: string;
    phone?: string;
  };
}

export const getFooterConfig = ({
  theme,
  t,
  loanOfficerData,
  branchData,
}: FooterConfigParams) => {
  // Determine if we have a specific loan officer selected
  const hasLoanOfficer = Boolean(loanOfficerData?.name);

  // Use loan officer data if available, then branch data, otherwise use default footer data
  const contactName = loanOfficerData?.name
    ? `Questions? Contact ${loanOfficerData.name}`
    : branchData?.name
      ? `Questions? Contact ${branchData.name}`
      : footerData.questionsContact;
  const contactEmail =
    loanOfficerData?.email || branchData?.email || footerData.email;
  const contactCellPhone =
    loanOfficerData?.phone || branchData?.phone || footerData.cellPhone;
  // Work phone only shown when no loan officer is selected
  const contactWorkPhone = footerData.workPhone;

  // Loan officer title (defaults to "Loan Officer")
  const contactTitle = loanOfficerData?.title || 'Loan Officer';

  // Loan officer NMLS with name - only shown when loan officer is present
  const loanOfficerNmlsDisplay =
    hasLoanOfficer && loanOfficerData?.nmls
      ? `${loanOfficerData.name} NMLS #${loanOfficerData.nmls}`
      : null;
  // Branch company name with NMLS (short version for top row)
  // Note: branchNmls from mapper already includes # prefix
  const branchCompanyNameShort = branchData?.branchNmls
    ? `Revolution Mortgage NMLS ${branchData.branchNmls}`
    : footerData.companyNameShort;

  // Company NMLS (full version for bottom row - only when no loan officer)
  const companyNmlsDisplay = !hasLoanOfficer ? footerData.companyName : null;

  // Text divider for inline pipe separators
  const TextDivider = () => (
    <Typography
      sx={{
        ...theme.typography.M3.body.small,
        color: theme.palette.M3?.sys?.dark?.outline,
      }}
    >
      |
    </Typography>
  );

  // Vertical divider component for links (uses outline 3 color - lighter)
  const LinkDivider = () => (
    <Divider
      orientation="vertical"
      flexItem
      sx={{
        borderWidth: theme.unit(0.25),
        borderColor: theme.palette.M3?.sys?.light?.['outline 3'],
        height: '16px',
        alignSelf: 'center',
      }}
    />
  );

  // Link text divider for mobile (uses dark outline like contact info)
  const LinkTextDivider = () => (
    <Typography
      sx={{
        ...theme.typography.M3.body.small,
        color: theme.palette.M3?.sys?.dark?.outline,
      }}
    >
      |
    </Typography>
  );

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.M3?.sys?.light?.['outline 2']}`,
        backgroundColor: 'transparent',
        width: '100%',
      }}
    >
      {/* Desktop Layout (>992px) */}
      <RowLayout
        sx={{
          display: { xs: 'none', md: 'none', lg: 'flex' },
          minHeight: '60px',
          paddingY: theme.unit(5),
          paddingX: theme.unit(6),
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          gap: theme.unit(4),
        }}
      >
        {/* Left Section - Footer Icon and Equal Housing Lender */}
        <RowLayout
          spacing="sm"
          sx={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={documentIcons.footerIcon}
            alt="Equal Housing Lender"
            sx={{ width: '30px', height: '24px' }}
          />
          <Typography
            sx={{
              ...theme.typography.M3.label.medium,
              fontWeight: 500,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {t('footer.equalHousingLender')}
          </Typography>
        </RowLayout>

        {/* Right Section - Two Rows */}
        <ColumnLayout
          spacing={2}
          sx={{
            alignItems: 'flex-end',
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Top Row - Contact Information */}
          <RowLayout
            spacing="sm"
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: theme.unit(1),
            }}
          >
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {contactName}
            </Typography>
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {contactTitle}
            </Typography>
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {contactEmail}
            </Typography>
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              Cell: {formatPhoneNumber(contactCellPhone)}
            </Typography>
            {/* Work phone only shown when no loan officer */}
            {!hasLoanOfficer && (
              <Typography
                sx={{
                  ...theme.typography.M3.body.small,
                  color: theme.palette.M3?.sys?.dark?.outline,
                }}
              >
                Work: {formatPhoneNumber(contactWorkPhone)}
              </Typography>
            )}
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {branchCompanyNameShort}
            </Typography>
            {/* Loan officer NMLS only shown when loan officer is present */}
            {loanOfficerNmlsDisplay && (
              <>
                <TextDivider />
                <Typography
                  sx={{
                    ...theme.typography.M3.body.small,
                    color: theme.palette.M3?.sys?.dark?.outline,
                  }}
                >
                  {loanOfficerNmlsDisplay}
                </Typography>
              </>
            )}
          </RowLayout>

          {/* Bottom Row - Company NMLS (if no loan officer) + Links */}
          <RowLayout
            spacing="sm"
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: theme.unit(1),
            }}
          >
            {/* Company NMLS only shown when no loan officer */}
            {companyNmlsDisplay && (
              <>
                <Typography
                  sx={{
                    ...theme.typography.M3.body.small,
                    color: theme.palette.M3?.sys?.dark?.outline,
                  }}
                >
                  {companyNmlsDisplay}
                </Typography>
                <TextDivider />
              </>
            )}
            <Box
              component="a"
              href="https://www.revolutionmortgage.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...theme.typography.M3.label.medium,
                fontWeight: 500,
                color: theme.palette.M3?.sys?.light?.['on-secondary-container'],
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('footer.privacyPolicy')}
            </Box>
            <LinkDivider />
            <Box
              component="a"
              href="https://www.revolutionmortgage.com/disclosures-and-licenses"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...theme.typography.M3.label.medium,
                fontWeight: 500,
                color: theme.palette.M3?.sys?.light?.['on-secondary-container'],
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('footer.revolutionLicensingAgreement')}
            </Box>
          </RowLayout>
        </ColumnLayout>
      </RowLayout>

      {/* Tablet Layout (768px - 992px) */}
      <RowLayout
        sx={{
          display: { xs: 'none', md: 'flex', lg: 'none' },
          paddingY: theme.unit(4),
          paddingX: theme.unit(10),
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {/* Left Section - Footer Icon and Equal Housing Lender */}
        <RowLayout
          spacing="sm"
          sx={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={documentIcons.footerIcon}
            alt="Equal Housing Lender"
            sx={{ width: '30px', height: '24px' }}
          />
          <Typography
            sx={{
              ...theme.typography.M3.label.medium,
              fontWeight: 500,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {t('footer.equalHousingLender')}
          </Typography>
        </RowLayout>

        {/* Right Section - Three Rows */}
        <ColumnLayout
          spacing={1}
          sx={{
            alignItems: 'flex-end',
          }}
        >
          {/* Row 1 - Name, Title, Email */}
          <RowLayout
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: theme.unit(2),
            }}
          >
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {contactName}
            </Typography>
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {contactTitle}
            </Typography>
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {contactEmail}
            </Typography>
          </RowLayout>

          {/* Row 2 - Phone, Company, NMLS */}
          <RowLayout
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: theme.unit(2),
            }}
          >
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              Cell: {formatPhoneNumber(contactCellPhone)}
            </Typography>
            {/* Work phone only shown when no loan officer */}
            {!hasLoanOfficer && (
              <Typography
                sx={{
                  ...theme.typography.M3.body.small,
                  color: theme.palette.M3?.sys?.dark?.outline,
                }}
              >
                Work: {formatPhoneNumber(contactWorkPhone)}
              </Typography>
            )}
            <TextDivider />
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {branchCompanyNameShort}
            </Typography>
            {/* Loan officer NMLS only shown when loan officer is present */}
            {loanOfficerNmlsDisplay && (
              <>
                <TextDivider />
                <Typography
                  sx={{
                    ...theme.typography.M3.body.small,
                    color: theme.palette.M3?.sys?.dark?.outline,
                  }}
                >
                  {loanOfficerNmlsDisplay}
                </Typography>
              </>
            )}
          </RowLayout>

          {/* Row 3 - Company NMLS (if no loan officer) + Links */}
          <RowLayout
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: theme.unit(2),
            }}
          >
            {/* Company NMLS only shown when no loan officer */}
            {companyNmlsDisplay && (
              <>
                <Typography
                  sx={{
                    ...theme.typography.M3.body.small,
                    color: theme.palette.M3?.sys?.dark?.outline,
                  }}
                >
                  {companyNmlsDisplay}
                </Typography>
                <TextDivider />
              </>
            )}
            <Box
              component="a"
              href="https://www.revolutionmortgage.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...theme.typography.M3.label.medium,
                fontWeight: 500,
                color: theme.palette.M3?.sys?.light?.primary,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('footer.privacyPolicy')}
            </Box>
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              |
            </Typography>
            <Box
              component="a"
              href="https://www.revolutionmortgage.com/disclosures-and-licenses"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...theme.typography.M3.label.medium,
                fontWeight: 500,
                color: theme.palette.M3?.sys?.light?.primary,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('footer.revolutionLicensingAgreement')}
            </Box>
          </RowLayout>
        </ColumnLayout>
      </RowLayout>

      {/* Mobile Layout (<768px) */}
      <ColumnLayout
        sx={{
          display: { xs: 'flex', md: 'none' },
          paddingY: theme.unit(3),
          paddingX: theme.unit(4),
          gap: theme.unit(2),
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {/* Equal Housing Lender */}
        <RowLayout
          spacing="sm"
          sx={{
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={documentIcons.footerIcon}
            alt="Equal Housing Lender"
            sx={{ width: '30px', height: '24px' }}
          />
          <Typography
            sx={{
              ...theme.typography.M3.label.medium,
              fontWeight: 500,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {t('footer.equalHousingLender')}
          </Typography>
        </RowLayout>

        {/* Row 1 - Contact Name | Title | */}
        <RowLayout
          sx={{
            alignItems: 'center',
            gap: theme.unit(1),
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {contactName}
          </Typography>
          <TextDivider />
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {contactTitle}
          </Typography>
          <TextDivider />
        </RowLayout>

        {/* Row 2 - Email | */}
        <RowLayout
          sx={{
            alignItems: 'center',
            gap: theme.unit(1),
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {contactEmail}
          </Typography>
          <TextDivider />
        </RowLayout>

        {/* Row 3 - Phone | Work Phone (if no LO) | Company | */}
        <RowLayout
          sx={{
            alignItems: 'center',
            gap: theme.unit(1),
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            Cell: {formatPhoneNumber(contactCellPhone)}
          </Typography>
          {/* Work phone only shown when no loan officer */}
          {!hasLoanOfficer && (
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              Work: {formatPhoneNumber(contactWorkPhone)}
            </Typography>
          )}
          <TextDivider />
          <Typography
            sx={{
              ...theme.typography.M3.body.small,
              color: theme.palette.M3?.sys?.dark?.outline,
            }}
          >
            {branchCompanyNameShort}
          </Typography>
          <TextDivider />
        </RowLayout>

        {/* Row 4 - Loan Officer NMLS | (if loan officer) */}
        {loanOfficerNmlsDisplay && (
          <RowLayout
            sx={{
              alignItems: 'center',
              gap: theme.unit(1),
            }}
          >
            <Typography
              sx={{
                ...theme.typography.M3.body.small,
                color: theme.palette.M3?.sys?.dark?.outline,
              }}
            >
              {loanOfficerNmlsDisplay}
            </Typography>
            <TextDivider />
          </RowLayout>
        )}

        {/* Row 5 - Company NMLS (if no loan officer) + Links: Privacy Policy | Revolution Licensing Agreement */}
        <RowLayout
          sx={{
            alignItems: 'center',
            gap: theme.unit(1),
            flexWrap: 'wrap',
          }}
        >
          {/* Company NMLS only shown when no loan officer */}
          {companyNmlsDisplay && (
            <>
              <Typography
                sx={{
                  ...theme.typography.M3.body.small,
                  color: theme.palette.M3?.sys?.dark?.outline,
                }}
              >
                {companyNmlsDisplay}
              </Typography>
              <TextDivider />
            </>
          )}
          <Box
            component="a"
            href="https://www.revolutionmortgage.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...theme.typography.M3.label.medium,
              fontWeight: 500,
              color: theme.palette.M3?.sys?.light?.['on-secondary-container'],
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('footer.privacyPolicy')}
          </Box>
          <LinkTextDivider />
          <Box
            component="a"
            href="https://www.revolutionmortgage.com/disclosures-and-licenses"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...theme.typography.M3.label.medium,
              fontWeight: 500,
              color: theme.palette.M3?.sys?.light?.['on-secondary-container'],
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('footer.revolutionLicensingAgreement')}
          </Box>
        </RowLayout>
      </ColumnLayout>
    </Box>
  );
};
